import React from 'react';

/**
 * Gemeinsame UI-Bausteine der Aktienanalyse (Ampel, Kacheln, Signal-Listen,
 * Abschnitts-Rahmen) im hellen Creme-Theme.
 *
 * Hinweis: recharts braucht echte Farbwerte (keine Tailwind-Klassen), daher hier
 * die Theme-Farben zusätzlich als Hex – synchron zu `tailwind.config.js` halten.
 */
export const farben = {
  ink: '#1c1a16',
  inkSoft: '#403d35',
  inkMuted: '#8b8473',
  line: '#e2dbcb',
  paper: '#f4f1e9',
  paperSoft: '#fbf9f3',
  gain: '#1c7c4a',
  loss: '#b23b3b',
  brand: '#3563ff',
  gold: '#9a7b1e',
};

/** Optik je Ampel-Stufe. */
const AMPEL = {
  GRUEN: { label: 'Positiv', chip: 'bg-gain/10 text-gain ring-gain/20', dot: 'bg-gain' },
  GELB: { label: 'Neutral', chip: 'bg-[#9a7b1e]/10 text-[#9a7b1e] ring-[#9a7b1e]/20', dot: 'bg-[#9a7b1e]' },
  ROT: { label: 'Kritisch', chip: 'bg-loss/10 text-loss ring-loss/20', dot: 'bg-loss' },
};

export function ampelMeta(ampel) {
  return AMPEL[ampel] || AMPEL.GELB;
}

/** Farbe eines Einzel-Signals anhand seiner Bewertung. */
export function bewertungKlasse(bewertung) {
  switch (bewertung) {
    case 'positiv':
    case 'gut':
      return 'text-gain';
    case 'negativ':
    case 'schwach':
      return 'text-loss';
    default:
      return 'text-ink-muted';
  }
}

function bewertungDot(bewertung) {
  switch (bewertung) {
    case 'positiv':
    case 'gut':
      return 'bg-gain';
    case 'negativ':
    case 'schwach':
      return 'bg-loss';
    default:
      return 'bg-ink-muted/50';
  }
}

/** Ampel-Chip mit optionalem Score. */
export function AmpelBadge({ ampel, score, size = 'md' }) {
  const m = ampelMeta(ampel);
  const padding = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full font-mono font-semibold uppercase tracking-wide ring-1 ${m.chip} ${padding}`}
    >
      <span className={`h-2 w-2 rounded-full ${m.dot}`} aria-hidden="true" />
      {m.label}
      {score != null && <span className="tabular-nums opacity-80">· {score}/100</span>}
    </span>
  );
}

/** Kennzahl-Kachel (Label + großer Wert + optionaler Hinweis). */
export function Kachel({ label, value, hint, tone }) {
  const toneClass =
    tone === 'gain' ? 'text-gain' : tone === 'loss' ? 'text-loss' : 'text-ink';
  return (
    <div className="rounded-xl border border-line bg-paper-soft px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={`mt-1 font-mono text-xl font-semibold tabular-nums ${toneClass}`}>
        {value ?? '–'}
      </div>
      {hint && <div className="mt-0.5 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

/** Eine Signal-Zeile (Punkt + Label + Wert + Erklärung). */
export function SignalRow({ signal }) {
  return (
    <li className="flex items-start gap-3 border-t border-line py-3 first:border-t-0">
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${bewertungDot(signal.bewertung)}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-ink">{signal.label}</span>
          <span className={`shrink-0 font-mono text-sm tabular-nums ${bewertungKlasse(signal.bewertung)}`}>
            {signal.wert}
          </span>
        </div>
        {signal.text && <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{signal.text}</p>}
      </div>
    </li>
  );
}

export function SignalListe({ signale }) {
  if (!signale || signale.length === 0) return null;
  return (
    <ul className="rounded-xl border border-line bg-paper-soft px-4">
      {signale.map((s, i) => (
        <SignalRow key={i} signal={s} />
      ))}
    </ul>
  );
}

/** Abschnitt mit Überschrift (für die Charts/Listen innerhalb eines Tabs). */
export function Abschnitt({ titel, children, aktion }) {
  return (
    <section className="mb-8">
      {titel && (
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-muted">
            {titel}
          </h3>
          {aktion}
        </div>
      )}
      {children}
    </section>
  );
}

/** Kurzer Fazit-/Erklärtext in dezenter Box. */
export function FazitBox({ children }) {
  if (!children) return null;
  return (
    <p className="rounded-xl border border-line bg-paper-sunken/60 p-4 text-sm leading-relaxed text-ink-soft">
      {children}
    </p>
  );
}

export function LeerHinweis({ children }) {
  return (
    <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
      {children}
    </p>
  );
}
